import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { trainingApi } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { ArrowLeft, LogOut, BookOpen, ExternalLink, CheckCircle2, Circle, Clock, GraduationCap, Sparkles } from 'lucide-react';
import { LogoHome } from '../components/LogoHome';
import { useConfirm } from '../components/ConfirmProvider';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_e3b3c70d-c72e-49dc-b41a-da6affdc6e46/artifacts/4w4rfom9_IMG_0777.PNG";

const CATEGORY_LABEL = {
  compliance: { label: 'Compliance', color: 'bg-rose-100 text-rose-700 border-rose-200' },
  communication: { label: 'Communication', color: 'bg-blue-100 text-blue-700 border-blue-200' },
  health: { label: 'Health', color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  cultural: { label: 'Cultural', color: 'bg-amber-100 text-amber-700 border-amber-200' },
  behaviour: { label: 'Behaviour', color: 'bg-purple-100 text-purple-700 border-purple-200' },
  safety: { label: 'Safety', color: 'bg-orange-100 text-orange-700 border-orange-200' },
};

export default function TrainingPage() {
  const confirm = useConfirm();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [modules, setModules] = useState([]);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);

  const loadAll = async () => {
    try {
      const [m, p] = await Promise.all([
        trainingApi.listModules(),
        trainingApi.getProgress(),
      ]);
      setModules(m.data);
      setProgress(p.data);
    } catch {
      toast.error('Failed to load training modules');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, []);

  const handleComplete = async (moduleId) => {
    try {
      await trainingApi.complete(moduleId);
      toast.success('Module marked as complete');
      loadAll();
    } catch (err) {
      toast.error('Failed');
    }
  };

  const handleReset = async (moduleId) => {
    if (!(await confirm({ title: 'Mark this module as not done?', confirmText: 'Reset' }))) return;
    try {
      await trainingApi.resetCompletion(moduleId);
      toast.success('Reset');
      loadAll();
    } catch {
      toast.error('Failed');
    }
  };

  const handleLogout = () => { logout(); navigate('/login'); };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 relative overflow-hidden" data-testid="training-page">
      {/* Decorative blobs */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -left-32 h-[28rem] w-[28rem] rounded-full bg-teal-400/30 blur-3xl animate-pulse-slow" />
        <div className="absolute top-1/3 right-0 h-80 w-80 rounded-full bg-emerald-500/25 blur-3xl animate-blob-float" />
        <div className="absolute -bottom-24 -right-24 h-[30rem] w-[30rem] rounded-full bg-cyan-300/30 blur-3xl animate-pulse-slow" style={{ animationDelay: '3s' }} />
      </div>

      <header className="bg-white/80 backdrop-blur-sm border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <LogoHome className="h-10 w-auto" />
            <div className="hidden sm:block">
              <h1 className="font-semibold text-slate-900 text-sm">Worker Training</h1>
              <p className="text-xs text-slate-500">{user?.name}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/dashboard" className="text-sm text-slate-500 hover:text-teal-600 flex items-center gap-1">
              <ArrowLeft className="h-4 w-4" /> Dashboard
            </Link>
            <Button variant="outline" size="sm" onClick={handleLogout}>
              <LogOut className="h-4 w-4 mr-1" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 relative z-10 space-y-6">
        {/* Hero / progress */}
        <Card className="border-2 border-teal-200 bg-gradient-to-br from-teal-50 to-white shadow-lg">
          <CardContent className="p-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-start gap-3">
                <div className="p-3 bg-teal-600 text-white rounded-xl shadow-md">
                  <GraduationCap className="h-7 w-7" />
                </div>
                <div>
                  <h2 className="font-heading text-xl font-bold text-slate-900">Micro-Training Modules</h2>
                  <p className="text-sm text-slate-600 mt-0.5">Curated NDIS-aligned training to keep you sharp on shift.</p>
                </div>
              </div>
              {progress && (
                <div className="text-right shrink-0 w-full sm:w-auto">
                  <p className="text-3xl font-bold text-teal-700 leading-none">
                    {progress.completed}<span className="text-lg text-slate-400">/{progress.total}</span>
                  </p>
                  <p className="text-xs text-slate-500 mt-1">{progress.percent}% complete</p>
                  <Progress value={progress.percent} className="w-full sm:w-48 h-2 mt-2" />
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Module list */}
        <div className="space-y-3">
          {modules.map(m => {
            const cat = CATEGORY_LABEL[m.category] || { label: m.category, color: 'bg-slate-100 text-slate-600' };
            const isExpanded = expandedId === m.id;
            return (
              <Card key={m.id} className={`transition-all ${m.completed ? 'border-emerald-200 bg-emerald-50/40' : 'border-slate-200 hover:border-teal-300'}`} data-testid={`training-module-${m.id}`}>
                <CardContent className="p-5">
                  <div className="flex items-start gap-3">
                    <button
                      onClick={() => m.completed ? handleReset(m.id) : handleComplete(m.id)}
                      className="shrink-0 mt-0.5"
                      data-testid={`complete-${m.id}`}
                      title={m.completed ? 'Mark as not done' : 'Mark as complete'}
                    >
                      {m.completed
                        ? <CheckCircle2 className="h-6 w-6 text-emerald-600" />
                        : <Circle className="h-6 w-6 text-slate-300 hover:text-teal-500" />}
                    </button>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                        <h3 className={`font-semibold ${m.completed ? 'text-slate-700 line-through' : 'text-slate-900'}`}>
                          {m.title}
                        </h3>
                        <div className="flex items-center gap-1.5 shrink-0">
                          <Badge variant="outline" className={`text-[10px] ${cat.color}`}>{cat.label}</Badge>
                          <Badge variant="outline" className="text-[10px] text-slate-500 border-slate-200 gap-1">
                            <Clock className="h-2.5 w-2.5" /> {m.duration_minutes} min
                          </Badge>
                        </div>
                      </div>
                      <p className="text-sm text-slate-600 mt-1">{m.description}</p>
                      {isExpanded && m.summary_points && (
                        <div className="mt-3 pl-3 border-l-2 border-teal-300">
                          <p className="text-xs font-medium text-teal-700 mb-1.5">Quick reference:</p>
                          <ul className="space-y-1">
                            {m.summary_points.map((p, i) => (
                              <li key={i} className="flex items-start gap-1.5 text-sm text-slate-700">
                                <Sparkles className="h-3 w-3 text-teal-500 shrink-0 mt-1" />
                                <span>{p}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      <div className="flex items-center gap-2 mt-3">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setExpandedId(isExpanded ? null : m.id)}
                          className="h-7 text-xs"
                          data-testid={`expand-${m.id}`}
                        >
                          <BookOpen className="h-3 w-3 mr-1" />
                          {isExpanded ? 'Hide summary' : 'Show summary'}
                        </Button>
                        <a
                          href={m.official_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`open-${m.id}`}
                        >
                          <Button variant="outline" size="sm" className="h-7 text-xs border-teal-300 text-teal-700 hover:bg-teal-50">
                            <ExternalLink className="h-3 w-3 mr-1" />
                            Open NDIS module
                          </Button>
                        </a>
                        {m.completed && (
                          <span className="text-[10px] text-emerald-700 ml-auto">
                            ✓ Completed {new Date(m.completed_at).toLocaleDateString('en-AU', { timeZone: 'Australia/Sydney' })}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </main>
    </div>
  );
}
